/**
 * Output Aggregator
 *
 * Builds a MonthlyOutputReport from normalized output records by:
 * 1. Splitting records into stool (outputCode === 1) and urine (outputCode === 2)
 * 2. Computing totals: totalOutputCount, totalStoolCount, totalUrineCount
 * 3. Computing normalOutputCount (typeCode === 1) and nonNormalOutputCount
 * 4. Grouping all records by dateKey
 * 5. For each date, building DailyOutputAggregate with:
 *    - stoolPatternEvents: individual events (no averaging) for stool records with patternCode > 0
 *    - urineColorEvents: individual events for all urine records
 *    - stoolAmountDistribution: count per amountScore
 *    - urineAmountDistribution: count per amountScore
 * 6. Sorting dailyAggregates by dateKey ascending
 * 7. Collecting outputExceptionEvents (typeCode !== 1)
 * 8. outputNotes: empty array (handled by notes aggregator)
 */

import {
  NormalizedOutputRecord,
  NormalizedNoteRecord,
  MonthlyOutputReport,
  DailyOutputAggregate,
} from '../types';

/**
 * Build a DailyOutputAggregate for a single date's records.
 */
function buildDailyAggregate(
  dateKey: string,
  dayRecords: NormalizedOutputRecord[]
): DailyOutputAggregate {
  const stoolRecords = dayRecords.filter(r => r.outputCode === 1);
  const urineRecords = dayRecords.filter(r => r.outputCode === 2);

  const normalOutputCount = dayRecords.filter(r => r.typeCode === 1).length;

  // Stool pattern events: individual dots for stool records with patternCode > 0
  const stoolPatternEvents = stoolRecords
    .filter(r => r.patternCode > 0)
    .map(r => ({
      recordedAt: r.recordedAt,
      patternCode: r.patternCode,
      patternLabel: r.patternLabel,
    }));

  // Urine color events: all urine records as individual events
  const urineColorEvents = urineRecords.map(r => ({
    recordedAt: r.recordedAt,
    colorCode: r.colorCode,
    colorLabel: r.colorLabel,
  }));

  // Stool amount distribution: count per amountScore
  const stoolAmountDistribution: Record<number, number> = {};
  for (const r of stoolRecords) {
    stoolAmountDistribution[r.amountScore] = (stoolAmountDistribution[r.amountScore] || 0) + 1;
  }

  // Urine amount distribution: count per amountScore
  const urineAmountDistribution: Record<number, number> = {};
  for (const r of urineRecords) {
    urineAmountDistribution[r.amountScore] = (urineAmountDistribution[r.amountScore] || 0) + 1;
  }

  return {
    dateKey,
    totalOutputCount: dayRecords.length,
    stoolCount: stoolRecords.length,
    urineCount: urineRecords.length,
    normalOutputCount,
    nonNormalOutputCount: dayRecords.length - normalOutputCount,
    stoolPatternEvents,
    urineColorEvents,
    stoolAmountDistribution,
    urineAmountDistribution,
    notes: [], // Notes are handled separately by the notes aggregator
  };
}

/**
 * Aggregate normalized output records into a MonthlyOutputReport.
 *
 * @param records - Array of normalized output records for the month
 * @param startDate - Start date of the report period (YYYY-MM-DD)
 * @param endDate - End date of the report period (YYYY-MM-DD)
 * @returns A MonthlyOutputReport with totals, daily aggregates, and exception events
 */
export function aggregateOutput(
  records: NormalizedOutputRecord[],
  startDate: string,
  endDate: string
): MonthlyOutputReport {
  const patientId = records.length > 0 ? records[0].patientId : '';

  // Split records: stool vs urine
  const totalStoolCount = records.filter(r => r.outputCode === 1).length;
  const totalUrineCount = records.filter(r => r.outputCode === 2).length;

  // Normal output: typeCode === 1
  const normalOutputCount = records.filter(r => r.typeCode === 1).length;
  const nonNormalOutputCount = records.length - normalOutputCount;

  // Group records by dateKey
  const dateGroups = new Map<string, NormalizedOutputRecord[]>();
  for (const record of records) {
    const existing = dateGroups.get(record.dateKey);
    if (existing) {
      existing.push(record);
    } else {
      dateGroups.set(record.dateKey, [record]);
    }
  }

  // Build daily aggregates
  const dailyAggregates: DailyOutputAggregate[] = [];
  for (const [dateKey, dayRecords] of dateGroups) {
    dailyAggregates.push(buildDailyAggregate(dateKey, dayRecords));
  }

  // Sort daily aggregates by dateKey ascending
  dailyAggregates.sort((a, b) => a.dateKey.localeCompare(b.dateKey));

  // Exception events: records where typeCode !== 1 (non-normal output types)
  const outputExceptionEvents = records.filter(r => r.typeCode !== 1);

  // Output notes: empty array (handled by notes aggregator)
  const outputNotes: NormalizedNoteRecord[] = [];

  return {
    patientId,
    startDate,
    endDate,
    totalOutputCount: records.length,
    totalStoolCount,
    totalUrineCount,
    normalOutputCount,
    nonNormalOutputCount,
    dailyAggregates,
    outputExceptionEvents,
    outputNotes,
  };
}
